import { Fragment, useEffect, useState } from 'react'
import { Button, Dropdown, Modal } from 'react-bootstrap'
import { connect, useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getApprovals,
  reviewUserWalletTopUpRequest,
  reviewTransactionLimitExtensionRequest,
} from '../../../services/TransactionService'
import { toast } from 'react-toastify'
import { readErrorEnvelope } from '../../../utils/errorEnvelope'
import Pagination from '../Pagination'
import { scrollToPageTop } from '../../../utils/scrollToPageTop'
import PinInput from '../PinInput'
import useGuidedTour from '../../../hooks/useGuidedTour'
import { hasPermission } from '../../../utils/auth/accessRules'
import { PERMISSION_SLUGS } from '../../../utils/auth/permissionConstants'
import {
  APPROVALS_OVERVIEW_TOUR_ID,
  buildApprovalsOverviewTourSteps,
} from './Approvals.tour'

const USER_WALLET_TOP_UP_SLUG = 'wallet-top-up'

const getLocalDateString = () => {
  const d = new Date()
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-')
}

const EMPTY_APPROVAL_FILTERS = { status: 'pending', type: '' }

const getDisplayAmount = (approval) => {
  return approval.requested_amount ?? '-'
}

const statusBadgeClass = (slug) => {
  if (slug === 'approved') return 'badge-success'
  if (slug === 'pending')  return 'badge-warning'
  return 'badge-danger'
}

const DeletedBadge = () => (
  <span className="badge badge-rounded badge-danger ms-1" style={{ fontSize: '0.65rem' }}>deleted</span>
)

const Approvals = ({ partnerId }) => {
  const queryClient = useQueryClient()
  const authUser = useSelector((state) => state.auth.authUser)

  const canReviewAnyApproval =
    hasPermission(authUser, PERMISSION_SLUGS.REVIEW_LIMIT_EXTENSION_REQUESTS) ||
    hasPermission(authUser, PERMISSION_SLUGS.REVIEW_WALLET_DISBURSEMENT_REQUESTS)

  useGuidedTour(
    APPROVALS_OVERVIEW_TOUR_ID,
    buildApprovalsOverviewTourSteps(),
    { enabled: canReviewAnyApproval },
  )

  const [approvalToReview, setApprovalToReview] = useState(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState(EMPTY_APPROVAL_FILTERS)
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(null)
  const [pinLockRetryAfterSeconds, setPinLockRetryAfterSeconds] = useState(null)

  const { status: queryStatus, data, error } = useQuery({
    queryKey: ['approvals', partnerId, filters, currentPage],
    queryFn: () => getApprovals(partnerId, filters, currentPage),
    enabled: !!partnerId,
    staleTime: 0,
  })

  const approvals = data?.data?.data || []
  const totalPages = data?.data?.meta?.last_page ?? 1

  useEffect(() => {
    setCurrentPage(1)
  }, [partnerId, filters])

  useEffect(() => {
    if (error) {
      toast.error('Failed to load approval requests. Please try again later.')
    }
  }, [error])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters(EMPTY_APPROVAL_FILTERS)
    setCurrentPage(1)
  }

  const hasActiveFilter = filters.status !== 'pending' || filters.type !== ''

  const onSuccess = async (response) => {
    const body = response?.data ?? {}
    if (body.outcome === 'held_for_wallet_top_up') {
      toast.info(body.message ?? 'Request is on hold pending a wallet top-up.')
    } else {
      toast.success('Approval request reviewed successfully.')
    }
    await queryClient.invalidateQueries({ queryKey: ['approvals', partnerId] })
    setShowReviewModal(false)
    setApprovalToReview(null)
  }

  const onError = (err) => {
    const { errorCode, meta, message } = readErrorEnvelope(err)
    if (errorCode === 'PIN_INVALID') {
      setPinAttemptsRemaining(meta.remaining_attempts ?? null)
      setPinLockRetryAfterSeconds(null)
    } else if (errorCode === 'PIN_LOCKED') {
      setPinLockRetryAfterSeconds(meta.retry_after_seconds ?? null)
      setPinAttemptsRemaining(null)
    }
    toast.error(message ?? 'Failed to review approval request. Please try again later.')
  }

  const limitExtensionMutation = useMutation({
    mutationFn: (payload) => reviewTransactionLimitExtensionRequest(partnerId, payload),
    onSuccess,
    onError,
  })

  const userWalletTopUpMutation = useMutation({
    mutationFn: (payload) => reviewUserWalletTopUpRequest(partnerId, payload),
    onSuccess,
    onError,
  })

  const handleReviewApproval = () => {
    if (!approvalToReview) return

    const isWalletTopUp = approvalToReview.approval_request_type_slug === USER_WALLET_TOP_UP_SLUG
    const isApproving = approvalToReview.decision === 'approved'

    const payload = {
      approval_id: approvalToReview.id,
      status: approvalToReview.decision,
      reject_reason: approvalToReview.decision === 'rejected' ? approvalToReview.reason : null,
      custom_expiry_date: isApproving ? approvalToReview.customExpiryDate || null : null,
    }

    if (isWalletTopUp) {
      if (isApproving) {
        payload.transaction_pin = approvalToReview.transactionPin
      }
      userWalletTopUpMutation.mutate(payload)
    } else {
      limitExtensionMutation.mutate(payload)
    }
  }

  const isSubmitting = limitExtensionMutation.isPending || userWalletTopUpMutation.isPending

  const openReviewModal = (approval) => {
    setApprovalToReview({ ...approval, decision: '', reason: '', customExpiryDate: '', transactionPin: '' })
    setPinAttemptsRemaining(null)
    setPinLockRetryAfterSeconds(null)
    setShowReviewModal(true)
  }

  const closeReviewModal = () => {
    setShowReviewModal(false)
    setApprovalToReview(null)
    setPinAttemptsRemaining(null)
    setPinLockRetryAfterSeconds(null)
  }

  if (!partnerId) return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body text-center py-5">
            <p className="text-muted mb-0">Select a partner to view this data.</p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card" data-tour="approvals-card">
            <div className="card-header">
              <h4 className="card-title mb-0">Approvals</h4>
            </div>
            <div className="card-body" data-tour="approvals-body">
              {(() => {
                const statusOptions = [
                  { value: 'pending',  label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                ]
                const typeOptions = [
                  { value: '',                              label: 'All types' },
                  { value: 'wallet-top-up',                 label: 'User Wallet Top-Up' },
                  { value: 'over-budget-for-transaction',   label: 'Over Budget for Transaction' },
                ]
                const currentStatusLabel = statusOptions.find(o => o.value === filters.status)?.label ?? 'Pending'
                const currentTypeLabel   = typeOptions.find(o => o.value === filters.type)?.label ?? 'All types'
                return (
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-3" data-tour="approvals-filters">
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {statusOptions.map(option => (
                          <Dropdown.Item
                            key={option.value}
                            active={filters.status === option.value}
                            onClick={() => handleFilterChange('status', option.value)}
                          >
                            {option.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{currentTypeLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {typeOptions.map(option => (
                          <Dropdown.Item
                            key={option.value || 'all'}
                            active={filters.type === option.value}
                            onClick={() => handleFilterChange('type', option.value)}
                          >
                            {option.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    {hasActiveFilter && (
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                        onClick={handleClearFilters}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )
              })()}
              <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }} data-tour="approvals-table">
                <thead className="thead-xl-only">
                  <tr>
                    <th scope="col" style={{ width: '20%' }}>Requester</th>
                    <th scope="col" style={{ width: '12%' }}>Type</th>
                    <th scope="col" style={{ width: '18%' }}>Amount / Limit</th>
                    <th scope="col" style={{ width: '26%' }}>Reason</th>
                    <th scope="col" style={{ width: '12%' }}>Status</th>
                    <th scope="col" style={{ width: '12%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {queryStatus === 'pending' ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : approvals.length > 0 ? (
                    approvals.map((approval) => (
                      <tr key={approval.id}>
                        <td>
                          <div className="d-flex align-items-center flex-wrap gap-3">
                            <span
                              title={approval.requester}
                              style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {approval.requester}
                              {approval.requester_deleted && (
                                <DeletedBadge />
                              )}
                            </span>
                            <span className="d-xl-none">
                              <span className={`badge badge-rounded ${statusBadgeClass(approval.status_slug)}`}>
                                {approval.status_name}
                              </span>
                            </span>
                          </div>
                          <div className="d-xl-none mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <div className="text-muted small">Type: {approval.approval_request_type}</div>
                            <div className="text-muted small">Amount / Limit: {getDisplayAmount(approval)}</div>
                            <div className="text-muted small">Reason: {approval.request_reason || '-'}</div>
                          </div>
                        </td>
                        <td className="d-none d-xl-table-cell text-nowrap">{approval.approval_request_type}</td>
                        <td className="d-none d-xl-table-cell text-nowrap">{getDisplayAmount(approval)}</td>
                        <td className="d-none d-xl-table-cell" style={{ maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {approval.request_reason || '-'}
                        </td>
                        <td className="d-none d-xl-table-cell">
                          <span className={`badge badge-rounded ${statusBadgeClass(approval.status_slug)}`}>
                            {approval.status_name}
                          </span>
                        </td>
                        <td className="text-end text-xl-start">
                          {approval.status_slug === 'pending' && approval.requester_id !== authUser?.id && (
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => openReviewModal(approval)}
                              aria-label="Review approval request"
                            >
                              <i className="fa fa-eye" aria-hidden="true" />
                            </button>
                          )}
                          {approval.status_slug === 'pending' && approval.requester_id === authUser?.id && (
                            <span
                              className="text-muted small"
                              title="This is a request you raised. Someone else on your account has to approve it."
                            >
                              Your request
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        No approval requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination page={currentPage} totalPages={totalPages} onPageChange={(nextPage) => { setCurrentPage(nextPage); scrollToPageTop(); }} />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showReviewModal} onHide={closeReviewModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Review Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approvalToReview && (
            <div className="mb-3">
              <p className="mb-1">
                <strong>Requester:</strong> {approvalToReview.requester}
                {approvalToReview.requester_deleted && (
                  <DeletedBadge />
                )}
              </p>
              <p className="mb-1">
                <strong>Type:</strong> {approvalToReview.approval_request_type}
              </p>
              <p className="mb-1">
                <strong>Amount / Limit:</strong> {getDisplayAmount(approvalToReview)}
              </p>
              {approvalToReview.request_reason && (
                <p className="mb-1">
                  <strong>Reason:</strong> {approvalToReview.request_reason}
                </p>
              )}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleReviewApproval()
            }}
          >
            <div className="mb-3">
              <label className="form-label">Decision</label>
              <select
                className="form-select"
                value={approvalToReview?.decision || ''}
                onChange={(e) =>
                  setApprovalToReview({
                    ...approvalToReview,
                    decision: e.target.value,
                    reason: e.target.value === 'rejected' ? approvalToReview.reason || '' : '',
                    customExpiryDate: '',
                  })
                }
                required
              >
                <option value="">Select…</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {approvalToReview?.decision === 'rejected' && (
              <div className="mb-3">
                <label className="form-label">Rejection Reason</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={approvalToReview?.reason || ''}
                  onChange={(e) =>
                    setApprovalToReview({ ...approvalToReview, reason: e.target.value })
                  }
                  required
                />
              </div>
            )}

            {approvalToReview?.is_per_transaction_limit && approvalToReview?.decision === 'approved' && (
              <div className="mb-3">
                <label className="form-label">Expiry Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  min={getLocalDateString()}
                  value={approvalToReview?.customExpiryDate || ''}
                  onChange={(e) =>
                    setApprovalToReview({ ...approvalToReview, customExpiryDate: e.target.value })
                  }
                  required
                />
              </div>
            )}

            {approvalToReview?.approval_request_type_slug === USER_WALLET_TOP_UP_SLUG
              && approvalToReview?.decision === 'approved' && (
              <div className="mb-3">
                <label className="form-label text-danger" htmlFor="approvalTransactionPin">
                  Enter Transaction PIN <span className="text-danger">*</span>
                </label>
                <PinInput
                  id="approvalTransactionPin"
                  name="approvalTransactionPin"
                  placeholder="Enter your PIN"
                  value={approvalToReview?.transactionPin || ''}
                  onChange={(e) =>
                    setApprovalToReview({ ...approvalToReview, transactionPin: e.target.value })
                  }
                  required
                />
                {pinAttemptsRemaining !== null && (
                  <small className={`mt-1 d-block ${pinAttemptsRemaining <= 1 ? 'text-danger' : 'text-warning'}`}>
                    {pinAttemptsRemaining === 0
                      ? 'No attempts remaining.'
                      : `${pinAttemptsRemaining} attempt${pinAttemptsRemaining === 1 ? '' : 's'} remaining.`}
                  </small>
                )}
                {pinLockRetryAfterSeconds !== null && (
                  <small className="mt-1 d-block text-danger">
                    Your PIN is locked. Try again in {Math.ceil(pinLockRetryAfterSeconds / 60)} minute{Math.ceil(pinLockRetryAfterSeconds / 60) === 1 ? '' : 's'}.
                  </small>
                )}
              </div>
            )}

            <div className="d-flex flex-wrap justify-content-end gap-2 pt-2">
              <Button variant="secondary" onClick={closeReviewModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  isSubmitting ||
                  !approvalToReview?.decision ||
                  (approvalToReview?.decision === 'rejected' && !approvalToReview?.reason?.trim()) ||
                  (approvalToReview?.is_per_transaction_limit && approvalToReview?.decision === 'approved' && !approvalToReview?.customExpiryDate) ||
                  (approvalToReview?.approval_request_type_slug === USER_WALLET_TOP_UP_SLUG && approvalToReview?.decision === 'approved' && !approvalToReview?.transactionPin)
                }
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Submitting…
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </Fragment>
  )
}

const mapStateToProps = (state) => ({
  partnerId: state.auth.authUser?.partner_id,
})

export default connect(mapStateToProps)(Approvals)
